import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { useLanguageUrlSync } from '../../hooks/useLanguageUrlSync';
import classes from './AuthLayout.module.css';

const LANGUAGES = [
  { value: 'no', label: 'Norsk', lang: 'nb' },
  { value: 'en', label: 'English', lang: 'en' },
];

const FOOTER_LINKS = [
  { key: 'privacy', href: 'https://trustpulse.no/personvern' },
  { key: 'help', href: 'mailto:hei@trustpulse.no' },
  { key: 'status', href: 'https://status.trustpulse.no' },
];

type AuthLayoutProps = {
  title: string;
  lead?: ReactNode;
  /** Bredere kort for skjema med mange felt, som registreringen */
  wide?: boolean;
  /** Vises under kortet, for eksempel lenken til registreringen */
  below?: ReactNode;
  /** Ved siden av kortet på brede skjermer, og under det på smale */
  aside?: ReactNode;
  children: ReactNode;
};

/** Felles side for innlogging, nytt passord og registrering: logo, språk, ett kort og lenker nederst */
export const AuthLayout = ({ title, lead, wide = false, below, aside, children }: AuthLayoutProps) => {
  const { t, i18n } = useTranslation();
  const { changeLanguage } = useLanguageUrlSync();

  return (
    <div className={classes.page}>
      <a href="#main" className="ds-skip-link">
        {t('auth.layout.skipToContent')}
      </a>
      <div aria-hidden="true" className={classes.arc} />

      <header className={classes.header}>
        <a href="https://trustpulse.no" className={classes.logo} aria-label={t('auth.layout.logoLabel')}>
          <img src="/trustpulse-horizontal.svg" alt="" />
        </a>
        <div role="group" aria-label={t('auth.layout.language')} className={classes.languages}>
          {LANGUAGES.map((language) => {
            const active = i18n.language === language.value;
            return (
              <Button
                key={language.value}
                type="button"
                data-size="sm"
                variant={active ? 'primary' : 'tertiary'}
                aria-pressed={active}
                lang={language.lang}
                className={classes.languageButton}
                onClick={() => changeLanguage(language.value)}
              >
                {language.label}
              </Button>
            );
          })}
        </div>
      </header>

      <main id="main" tabIndex={-1} className={classes.main}>
        {/* Et kort skjema trenger ikke appens store standardstørrelse; sm gir felt og knapper i vanlig høyde */}
        <div className={classes.columns} data-aside={aside ? true : undefined}>
        <div className={classes.card} data-wide={wide} data-size="sm">
          <div className={classes.intro}>
            <Heading level={1} data-size="md">
              {title}
            </Heading>
            {lead && (
              <Paragraph data-size="md" className={classes.lead}>
                {lead}
              </Paragraph>
            )}
          </div>
          {children}
        </div>
        {aside && <div className={classes.aside} data-size="sm">{aside}</div>}
        </div>
        {below && <div className={classes.below}>{below}</div>}
      </main>

      <footer className={classes.footer}>
        <ul className={classes.footerLinks}>
          {FOOTER_LINKS.map(({ key, href }) => (
            <li key={key}>
              <a href={href} className="ds-link" data-size="sm">
                {t(`auth.layout.${key}`)}
              </a>
            </li>
          ))}
        </ul>
      </footer>
    </div>
  );
};
